/**
 * Preview updates, coalesced per frame and posted without waiting.
 *
 * WHAT IT REPLACES. Every panel dispatched its own `sendCommand` per edit — an
 * awaited round trip that registers a `message` listener and arms a five-second
 * timeout for each keystroke, then tears both down again. None of that is needed
 * to move a sprite: the preview is a rendering concern, and a frame that never
 * arrives is corrected by the next one.
 *
 * COALESCING BY KEY, not by path. A key names the thing being previewed —
 * one component's `text`, one global variable in one language, the general
 * settings block — and a later push with the same key replaces the earlier one.
 * That is what makes a burst of keystrokes cost one message instead of eight,
 * and it is correct because these commands are last-write-wins by nature.
 *
 * ORDERING. `postMessage` preserves order per window, so commands flushed in one
 * frame arrive in the order they were queued. Anything that must land after a
 * queued edit — a language switch, a snapshot reload — calls `flush` first
 * rather than racing it.
 */

const IDLE_FLUSH_MS = 250;

let requestCounter = 0;

function nextRequestId() {
  requestCounter += 1;
  return `preview-${Date.now().toString(36)}-${requestCounter}`;
}

/**
 * How the queue finds the window to post to.
 *
 * Injectable so the queue holds no assumption about where the preview lives —
 * the version editor and the snapshot editor already name different iframes —
 * and so it stays runnable, and testable, without a DOM.
 */
let resolveTarget = (iFrameId) => {
  if (typeof document === "undefined") return null;
  const iframe = document.getElementById(iFrameId);
  return (iframe && iframe.contentWindow) || null;
};

/**
 * Replace the target lookup.
 *
 * @param {{ resolveTarget?: (iFrameId: string) => (Window|null) }} options
 */
export function configurePreviewQueue({ resolveTarget: next } = {}) {
  if (typeof next === "function") resolveTarget = next;
}

/** @type {Map<string, object>} iFrameId → { pending: Map<key, message>, frame, timer }*/
const queues = new Map();

function getQueue(iFrameId) {
  const id = String(iFrameId || "");
  if (!queues.has(id)) {
    queues.set(id, { iFrameId: id, pending: new Map(), frame: null, timer: null });
  }
  return queues.get(id);
}

function cancelScheduled(queue) {
  if (queue.frame !== null && typeof cancelAnimationFrame === "function") {
    cancelAnimationFrame(queue.frame);
  }
  if (queue.timer !== null) clearTimeout(queue.timer);
  queue.frame = null;
  queue.timer = null;
}

function schedule(queue) {
  if (queue.frame !== null || queue.timer !== null) return;

  // A hidden tab gets no animation frames, and an edit made through the agent or
  // a keyboard shortcut while the window is in the background would sit in the
  // queue indefinitely. Fall back to a timer, which keeps firing.
  const isHidden = typeof document !== "undefined" && document.hidden;
  if (!isHidden && typeof requestAnimationFrame === "function") {
    queue.frame = requestAnimationFrame(() => {
      queue.frame = null;
      flush(queue.iFrameId);
    });
    return;
  }
  queue.timer = setTimeout(() => {
    queue.timer = null;
    flush(queue.iFrameId);
  }, IDLE_FLUSH_MS);
}

/**
 * Queue one preview command. A later push with the same `key` replaces this one.
 *
 * @param {string} iFrameId
 * @param {string} key coalescing identity, e.g. "config:hero1:text"
 * @param {object} message the postMessage payload, as the engine expects it
 */
export function push(iFrameId, key, message) {
  if (!message) return;
  const queue = getQueue(iFrameId);
  queue.pending.set(String(key), message);
  schedule(queue);
}

/**
 * Post everything queued for this iframe, now.
 *
 * Synchronous, so a caller can order a later command after the queued ones
 * simply by flushing before sending it.
 *
 * @param {string} iFrameId
 * @returns {number} how many messages were posted
 */
export function flush(iFrameId) {
  const queue = queues.get(String(iFrameId || ""));
  if (!queue) return 0;

  cancelScheduled(queue);
  if (queue.pending.size === 0) return 0;

  const messages = [...queue.pending.values()];
  queue.pending.clear();

  const target = resolveTarget(queue.iFrameId);
  if (!target) {
    // The preview is reloading or gone. Dropping is right: whatever comes back
    // is loaded from the snapshot, which already carries these values.
    return 0;
  }

  for (const message of messages) {
    try {
      // The engine answers commands by `requestId`, and nothing here is
      // listening for the answer. It is stamped anyway so the message stays
      // exactly the shape the engine has always received — what this queue
      // removes is the listener, the promise and the five-second timeout, not
      // the protocol.
      target.postMessage({ ...message, requestId: nextRequestId() }, "*");
    } catch (err) {
      // A cross-origin or torn-down frame. The next edit will try again.
      console.warn("[previewQueue] postMessage failed:", err && err.message);
    }
  }
  return messages.length;
}

/** Drop what is queued without sending — the preview is being replaced anyway. */
export function discard(iFrameId) {
  const queue = queues.get(String(iFrameId || ""));
  if (!queue) return;
  cancelScheduled(queue);
  queue.pending.clear();
}

/** Coalescing keys, so the panels cannot disagree about what "the same field" is. */
export const previewKey = {
  /** One component config field. */
  config: (componentId, field) => `config:${componentId}:${field}`,
  /** One component config field, as a translation. */
  configOverride: (language, componentId, field) =>
    `configOverride:${language}:${componentId}:${field}`,
  /** One global variable, in the base language or as a translation. */
  variable: (language, path) => `variable:${language || "en"}:${path}`,
  /** The general settings block, which the engine only takes whole. */
  generalSettings: () => "generalSettings",
};

/** Tests only. */
export function resetPreviewQueue() {
  for (const queue of queues.values()) cancelScheduled(queue);
  queues.clear();
}

/** Tests only. */
export function pendingCount(iFrameId) {
  const queue = queues.get(String(iFrameId || ""));
  return queue ? queue.pending.size : 0;
}
